import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
    BookOpen,
    Mail,
    Megaphone,
    Globe,
    Layout,
    Type,
    Languages,
    ArrowRight,
    Quote,
    Linkedin,
    Phone,
    ExternalLink,
    FileText,
    ImageIcon,
    Download,
} from 'lucide-react';
import {
    listSamples,
    submitContact,
    CONTENT_TYPES,
    PROJECT_TYPES,
} from '@/lib/api';
import { cn, hasBengali } from '@/lib/utils';
import PortfolioPDF from '@/pages/PortfolioPDF';
import { generatePortfolioPDF } from '@/lib/generatePortfolioPDF';

const NAV_ITEMS = [
    { id: 'home', label: 'Home' },
    { id: 'work', label: 'Work' },
    { id: 'about', label: 'About' },
    { id: 'clients', label: 'Clients' },
    { id: 'contact', label: 'Contact' },
];

const CATEGORY_CARDS = [
    {
        type: 'Blog',
        icon: BookOpen,
        desc: 'Long-form pieces that respect the reader’s time and the bureau curtain. SEO when needed, voice always.',
    },
    {
        type: 'Newsletter',
        icon: Mail,
        desc: 'Weekly inboxes built to ship. Fifty issues of Money Moves and counting — no skipped weeks.',
    },
    {
        type: 'Press Release',
        icon: Megaphone,
        desc: 'Wire-ready announcements that journalists actually quote. Quotes on file, embargoes respected.',
    },
    {
        type: 'Website Copy',
        icon: Globe,
        desc: 'Homepages, services, manifestos. Brand voice that survives a junior PM’s redline.',
    },
    {
        type: 'Landing Page',
        icon: Layout,
        desc: 'Acquisition pages built for cold paid traffic. Hooks that earn the second scroll.',
    },
    {
        type: 'UI Microcopy',
        icon: Type,
        desc: 'Empty states, error states, onboarding. The 12-word work that decides whether anyone returns.',
    },
    {
        type: 'Bilingual',
        icon: Languages,
        desc: 'English and Bengali, written natively in both. Localisation is a product project, not a translation memo.',
        bilingual: true,
    },
];

const CLIENTS = [
    'BankBazaar',
    'Flipkart',
    'Fortune India',
    'Salt Audios',
    'Notion Press',
    'Xanadu',
    'Jar',
    'Design Cafe',
    'Gizbot',
    'Silicon India',
    'Infiniti Research',
];

const TESTIMONIALS = [
    {
        quote:
            'Pallabi turned our launch comms around in five days flat. The Bengali version was sharper than our English original — which never happens.',
        author: 'Product Lead',
        role: 'Fintech, Bengaluru',
    },
    {
        quote:
            'A weekly newsletter that actually gets opened. Subject lines that read like a friend texting, not a brand emailing.',
        author: 'Head of Growth',
        role: 'Consumer SaaS',
    },
    {
        quote:
            'The rare writer who pushes back when the brief is wrong, and ships when the brief is right. Hire her before someone else does.',
        author: 'Creative Director',
        role: 'Boutique Studio',
    },
];

function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    return (
        <nav
            className={cn(
                'sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur transition-shadow',
                scrolled && 'shadow-soft'
            )}
            data-testid="public-nav"
        >
            <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3">
                <a
                    href="#home"
                    className="font-heading text-lg sm:text-xl font-semibold tracking-tight"
                    data-testid="nav-logo"
                >
                    Pallabi Bose
                </a>
                <div className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
                    {NAV_ITEMS.map((n) => (
                        <a
                            key={n.id}
                            href={`#${n.id}`}
                            className="hover:text-foreground transition-colors"
                            data-testid={`nav-link-${n.id}`}
                        >
                            {n.label}
                        </a>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        asChild
                        className="rounded-full hidden sm:inline-flex"
                        data-testid="nav-cta-lets-talk"
                    >
                        <a href="#contact">Let’s Talk</a>
                    </Button>
                    <button
                        className="md:hidden text-sm text-muted-foreground hover:text-foreground"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                    >
                        Menu
                    </button>
                </div>
            </div>
            {open && (
                <div className="md:hidden border-t border-border bg-background">
                    <div className="mx-auto max-w-6xl px-4 py-3 flex flex-col gap-3 text-sm">
                        {NAV_ITEMS.map((n) => (
                            <a
                                key={n.id}
                                href={`#${n.id}`}
                                onClick={() => setOpen(false)}
                                className="py-1 text-muted-foreground hover:text-foreground"
                            >
                                {n.label}
                            </a>
                        ))}
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            className="py-1 font-medium text-primary"
                        >
                            Let’s Talk →
                        </a>
                    </div>
                </div>
            )}
        </nav>
    );
}

function Hero({ onDownloadPDF, downloading }) {
    return (
        <section
            id="home"
            className="relative overflow-hidden"
            data-testid="hero-section"
        >
            <div className="absolute inset-0 hero-wash pointer-events-none" />
            <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-24 lg:pb-28">
                <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
                    <div>
                        <p className="kicker" data-testid="hero-kicker">
                            Editorial · Bilingual · Available for select projects
                        </p>
                        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.02] tracking-[-0.02em]">
                            Words that work.
                            <br />
                            <span className="text-primary">In two languages.</span>
                        </h1>
                        <p className="mt-6 max-w-[54ch] text-base sm:text-lg leading-relaxed text-muted-foreground">
                            I’m Pallabi Bose — a content writer and
                            client-servicing expert with 11+ years across BankBazaar, Flipkart, and a
                            long list of B2B and B2C brands. I write blogs,
                            newsletters, press releases, and UI microcopy in English
                            and <span className="font-bn">বাংলা</span>.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Button
                                asChild
                                size="lg"
                                className="rounded-full px-6"
                                data-testid="hero-see-my-work-button"
                            >
                                <a href="#work">
                                    See My Work
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </a>
                            </Button>
                            <Button
                                asChild
                                size="lg"
                                variant="outline"
                                className="rounded-full px-6 bg-card"
                                data-testid="hero-lets-talk-button"
                            >
                                <a href="#contact">Let’s Talk</a>
                            </Button>
                            <Button
                                type="button"
                                size="lg"
                                variant="ghost"
                                disabled={downloading}
                                onClick={onDownloadPDF}
                                className="rounded-full px-6"
                                data-testid="hero-download-pdf-button"
                            >
                                <Download className="mr-2 h-4 w-4" />
                                {downloading
                                    ? 'Preparing PDF…'
                                    : 'Download PDF'}
                            </Button>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="relative rounded-[var(--radius-card)] border border-border bg-card shadow-card overflow-hidden">
                            <div className="absolute inset-0 noise" />
                            <img
                                src="https://images.pexels.com/photos/7657415/pexels-photo-7657415.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Editorial desk flatlay"
                                className="h-full w-full object-cover aspect-[4/5]"
                                loading="lazy"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 via-background/40 to-transparent p-4">
                                <p className="kicker">From the desk of</p>
                                <p className="font-heading text-lg leading-tight">
                                    Pallabi Bose
                                </p>
                            </div>
                        </div>
                        <div className="mt-3 text-xs text-muted-foreground italic">
                            Cover image — representative editorial flatlay.
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function WhatIDo() {
    return (
        <section
            id="what-i-do"
            className="py-16 sm:py-20 lg:py-24"
            data-testid="what-i-do-section"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="flex items-end justify-between gap-4 mb-8">
                    <div>
                        <p className="kicker">§ What I do</p>
                        <h2 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight">
                            Seven shapes of writing.
                        </h2>
                    </div>
                    <p className="hidden md:block max-w-sm text-sm text-muted-foreground">
                        Each shape comes with its own register, its own constraints,
                        and its own definition of “done”. I work across all seven.
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
                    {CATEGORY_CARDS.map((c) => {
                        const Icon = c.icon;
                        return (
                            <div
                                key={c.type}
                                className="group relative rounded-[var(--radius-card)] border border-border bg-card p-5 sm:p-6 transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-soft"
                                data-testid="what-i-do-card"
                            >
                                {c.bilingual && (
                                    <span
                                        className="absolute right-4 top-4 font-heading text-2xl opacity-25 select-none"
                                        aria-hidden
                                    >
                                        <span className="font-bn">অ</span> / A
                                    </span>
                                )}
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-foreground">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="mt-5 font-heading text-lg leading-snug">
                                    {c.type}
                                </h3>
                                <div className="editorial-rule mt-3" />
                                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                                    {c.desc}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function SampleCard({ sample, onOpen }) {
    const isBn = sample.language === 'Bengali' || sample.language === 'Both';
    const previewImageUrl =
        (sample.attachmentMime || '').startsWith('image/') &&
        (sample.attachmentUrl || sample.attachmentBase64);
    const ctaLabel = sample.redirectOnly ? 'View Live Page' : 'Read Sample';

    function handleOpen() {
        if (sample.redirectOnly && sample.externalUrl) {
            window.open(sample.externalUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        onOpen(sample);
    }

    return (
        <div
            className="group rounded-[var(--radius-card)] border border-border bg-card overflow-hidden transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:shadow-soft hover:border-primary/30 flex flex-col"
            data-testid="portfolio-card"
        >
            {previewImageUrl && (
                <button
                    type="button"
                    onClick={handleOpen}
                    className="block relative aspect-[16/10] w-full overflow-hidden bg-muted/40 border-b border-border"
                    aria-label={`Open ${sample.title}`}
                >
                    <img
                        src={previewImageUrl}
                        alt={sample.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                </button>
            )}
            <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2">
                    <span className="kicker">{sample.contentType}</span>
                    {isBn && (
                        <Badge
                            variant="secondary"
                            className="rounded-full text-[10px]"
                        >
                            <span className="font-bn mr-1">অ</span>/A
                        </Badge>
                    )}
                    {sample.redirectOnly && (
                        <Badge
                            variant="outline"
                            className="rounded-full text-[10px] inline-flex items-center gap-1"
                        >
                            <ExternalLink className="h-3 w-3" />
                            Live
                        </Badge>
                    )}
                </div>
                <h3
                    className={cn(
                        'mt-2 font-heading text-xl leading-snug',
                        hasBengali(sample.title) && 'font-bn'
                    )}
                >
                    {sample.title}
                </h3>
                <p
                    className={cn(
                        'mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-4 flex-1',
                        hasBengali(sample.excerpt) && 'leading-[1.75]'
                    )}
                >
                    {sample.excerpt}
                </p>
                <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                        {sample.language}
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full -mr-2"
                        onClick={handleOpen}
                        data-testid="portfolio-read-sample-button"
                    >
                        {ctaLabel}
                        <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

function SampleModal({ sample, onClose }) {
    if (!sample) return null;
    const titleHasBn = hasBengali(sample.title);
    const contentHasBn = hasBengali(sample.content);
    const attachmentSrc = sample.attachmentUrl || sample.attachmentBase64 || null;
    const isImage =
        attachmentSrc && (sample.attachmentMime || '').startsWith('image/');
    const isPdf =
        attachmentSrc && sample.attachmentMime === 'application/pdf';
    const renderHtml = !!sample.htmlContent;

    return (
        <Dialog open={!!sample} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-3xl rounded-[22px] p-0 overflow-hidden gap-0"
                data-testid="portfolio-sample-dialog"
            >
                <DialogHeader className="border-b border-border bg-background/90 backdrop-blur px-6 py-4 text-left space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary" className="rounded-full">
                            {sample.contentType}
                        </Badge>
                        <Badge variant="outline" className="rounded-full">
                            {sample.language}
                        </Badge>
                        {sample.externalUrl && (
                            <a
                                href={sample.externalUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-auto text-xs text-primary hover:underline inline-flex items-center gap-1"
                                data-testid="portfolio-sample-external-link"
                            >
                                View live <ExternalLink className="h-3 w-3" />
                            </a>
                        )}
                    </div>
                    <DialogTitle
                        className={cn(
                            'font-heading text-2xl sm:text-3xl tracking-tight leading-tight',
                            titleHasBn && 'font-bn'
                        )}
                    >
                        {sample.title}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        {sample.excerpt}
                    </p>
                </DialogHeader>
                <div className="max-h-[72vh] overflow-y-auto">
                    {renderHtml ? (
                        <div className="bg-[#f4f4f4] p-4 sm:p-6">
                            <iframe
                                srcDoc={sample.htmlContent}
                                title={sample.title}
                                className="w-full bg-white rounded-md border border-border"
                                style={{ height: '70vh' }}
                                sandbox="allow-same-origin"
                                data-testid="portfolio-sample-html-iframe"
                            />
                        </div>
                    ) : (
                        <div className="px-6 py-6">
                            <article
                                className={cn(
                                    'reading-prose max-w-prose',
                                    contentHasBn &&
                                        'font-bn reading-prose-bn'
                                )}
                            >
                                {sample.content}
                            </article>
                            {isImage && (
                                <div className="mt-6 rounded-xl border border-border bg-muted/40 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="kicker flex items-center gap-2">
                                            <ImageIcon className="h-3.5 w-3.5" />
                                            Mockup
                                        </p>
                                        <a
                                            href={attachmentSrc}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs text-primary hover:underline"
                                        >
                                            Open full size
                                        </a>
                                    </div>
                                    <img
                                        src={attachmentSrc}
                                        alt={
                                            sample.attachmentName ||
                                            sample.title
                                        }
                                        className="w-full max-h-[600px] object-contain rounded-md bg-card"
                                    />
                                </div>
                            )}
                            {isPdf && (
                                <div className="mt-6 rounded-xl border border-border bg-muted/40 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="kicker flex items-center gap-2">
                                            <FileText className="h-3.5 w-3.5" />
                                            PDF Attachment
                                        </p>
                                        <a
                                            href={attachmentSrc}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs text-primary hover:underline"
                                        >
                                            Open PDF in new tab
                                        </a>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {sample.attachmentName ||
                                            'press-release.pdf'}
                                    </p>
                                </div>
                            )}
                            {attachmentSrc && !isImage && !isPdf && (
                                <div className="mt-6 rounded-xl border border-border bg-muted/40 p-4">
                                    <a
                                        href={attachmentSrc}
                                        download={sample.attachmentName}
                                        className="text-sm text-primary hover:underline"
                                    >
                                        Download attachment
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PortfolioGrid({ samples, loading, onOpen, filter, setFilter }) {
    const filtered = useMemo(() => {
        if (filter === 'All') return samples;
        return samples.filter((s) => s.contentType === filter);
    }, [filter, samples]);
    return (
        <section
            id="work"
            className="py-16 sm:py-20 lg:py-24 border-t border-border"
            data-testid="portfolio-section"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
                    <div>
                        <p className="kicker">§ Selected work</p>
                        <h2 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight">
                            The portfolio.
                        </h2>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-sm">
                        Filter by content type. Each piece opens as a reading
                        sheet.
                    </p>
                </div>
                <Tabs value={filter} onValueChange={setFilter} className="w-full">
                    <TabsList
                        className="flex flex-wrap h-auto bg-transparent p-0 gap-2 mb-8"
                        data-testid="portfolio-filter-tabs"
                    >
                        {['All', ...CONTENT_TYPES].map((t) => (
                            <TabsTrigger
                                key={t}
                                value={t}
                                className="rounded-full border border-border bg-card data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary px-4 py-1.5 text-xs font-medium uppercase tracking-[0.14em]"
                                data-testid={`filter-pill-${t.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                {t}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
                {loading ? (
                    <div className="text-sm text-muted-foreground">
                        Loading samples…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-8 text-center">
                        No samples in this category yet.
                    </div>
                ) : (
                    <div
                        className="grid gap-5 md:grid-cols-2 lg:grid-cols-3"
                        data-testid="portfolio-grid"
                    >
                        {filtered.map((s) => (
                            <SampleCard
                                key={s.id}
                                sample={s}
                                onOpen={onOpen}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function About() {
    return (
        <section
            id="about"
            className="py-16 sm:py-20 lg:py-24 border-t border-border"
            data-testid="about-section"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <p className="kicker">§ About</p>
                <h2 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight mb-10">
                    The writer behind the desk.
                </h2>
                <div className="grid gap-10 md:grid-cols-[0.85fr_1.15fr] md:items-start">
                    <div className="rounded-[var(--radius-card)] border border-border bg-card p-4">
                        <div className="relative aspect-[4/5] w-full rounded-xl overflow-hidden">
                            <img
                                src="https://customer-assets.emergentagent.com/job_content-showcase-68/artifacts/fss76x65_1707645870222.jpg"
                                alt="Pallabi Bose"
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="lazy"
                            />
                            <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-foreground/80 via-foreground/30 to-transparent">
                                <p className="kicker text-background/80">Photo</p>
                                <p className="font-heading text-lg leading-tight text-background">
                                    Pallabi Bose
                                </p>
                            </div>
                        </div>
                        <div className="mt-4 text-xs text-muted-foreground">
                            Kolkata · Available for select projects · EN / BN
                        </div>
                    </div>
                    <div>
                        <p className="font-heading text-2xl sm:text-3xl leading-snug">
                            I write for the moment between intent and action —
                            the inbox at 9am, the empty cart at midnight, the
                            press release that lands at 6pm.
                        </p>
                        <Separator className="my-6" />
                        <div className="space-y-4 text-sm sm:text-base leading-relaxed text-foreground/90 max-w-prose">
                            <p>
                                I’ve spent the last decade in the rooms where
                                content meets product — currently as Sr.
                                Product Content Analyst at BankBazaar, writing across
                                credit, savings and launch-stage products for
                                50M+ users.
                            </p>
                            <p>
                                Before BankBazaar, I spent four-and-a-half years
                                writing UI/UX copy for the Bengali Flipkart App,
                                in-app content, brand ads, video commerce, and
                                Flipkart Grocery. Earlier, I produced regional
                                radio campaigns at Salt Audios, edited indie
                                manuscripts at Notion Press, covered tech
                                hiring at Fortune India, and wrote 50+ vendor
                                profiles at Silicon India’s CIO Review.
                            </p>
                            <p>
                                As a freelancer I’ve worked with 15+ B2B and
                                B2C brands — 150+ blogs, 20+ press releases,
                                10+ website copies, zero plagiarism. I write
                                bilingually because Bengali is not my translation
                                target. It’s my source language too.
                            </p>
                        </div>
                        <div className="mt-6 grid gap-3 sm:grid-cols-2">
                            <div className="rounded-[var(--radius-card)] border border-border bg-card p-5">
                                <p className="kicker">Notable launch</p>
                                <p className="mt-2 font-heading text-base leading-snug">
                                    CreditStrong — Regional Language Platform (BankBazaar product)
                                </p>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    Bootstrapped the regional-language launch
                                    end to end — UI content plus promotional
                                    content across WhatsApp, email and SMS.
                                    Shipped the Bengali version first; served
                                    as content SPOC for the rollout across
                                    9 Indian languages.
                                </p>
                            </div>
                            <div className="rounded-[var(--radius-card)] border border-border bg-card p-5">
                                <p className="kicker">Flagship property</p>
                                <p className="mt-2 font-heading text-base leading-snug">
                                    BankBazaar Money Moves — Newsletter
                                </p>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    Launched and continue to write the weekly
                                    consumer-finance newsletter. 50+ issues
                                    shipped, built specifically to reactivate
                                    a snoozing subscriber base with sharp,
                                    useful takes on money.
                                </p>
                            </div>
                        </div>
                        <div className="mt-8 rounded-[var(--radius-card)] border border-border bg-accent/40 p-5">
                            <Quote className="h-5 w-5 text-primary" />
                            <p className="mt-2 font-heading text-lg leading-snug">
                                The same screen will be 10–18% longer in
                                Bengali. Don’t compress — design the screen for the
                                longer form.
                            </p>
                            <p className="mt-3 kicker">
                                From an internal voice guide
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Clients() {
    return (
        <section
            id="clients"
            className="py-16 sm:py-20 lg:py-24 border-t border-border"
            data-testid="clients-section"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <p className="kicker">§ Trusted by</p>
                <h2 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight">
                    Brands I’ve written for.
                </h2>
                <p className="mt-3 max-w-prose text-sm text-muted-foreground">
                    Across staff roles, freelance retainers, and one-off projects —
                    a partial list.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {CLIENTS.map((c) => (
                        <div
                            key={c}
                            className="flex items-center justify-center rounded-xl border border-border bg-card px-3 py-5 text-sm font-heading font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                            data-testid="client-logo"
                        >
                            {c}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Testimonials() {
    return (
        <section
            id="testimonials"
            className="py-16 sm:py-20 lg:py-24 border-t border-border"
            data-testid="testimonials-section"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <p className="kicker">§ Testimonials</p>
                <h2 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight mb-10">
                    What collaborators have said.
                </h2>
                <div className="grid gap-4 md:grid-cols-3">
                    {TESTIMONIALS.map((t, i) => (
                        <figure
                            key={i}
                            className="rounded-[var(--radius-card)] border border-border bg-card p-6"
                        >
                            <Quote className="h-5 w-5 text-primary" />
                            <blockquote className="mt-3 text-sm leading-relaxed text-foreground/90">
                                “{t.quote}”
                            </blockquote>
                            <figcaption className="mt-5 kicker">
                                {t.author} · {t.role}
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Contact() {
    const [form, setForm] = useState({
        name: '',
        email: '',
        projectType: '',
        message: '',
    });
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!form.name || !form.email || !form.projectType || !form.message) {
            toast.error('Please fill out all fields before sending.');
            return;
        }
        try {
            setSubmitting(true);
            await submitContact(form);
            toast.success(
                'Thanks — your note landed in my inbox. I’ll reply within 48 hours.'
            );
            setForm({ name: '', email: '', projectType: '', message: '' });
        } catch (err) {
            const detail =
                err?.response?.data?.detail || 'Could not send your note. Try again?';
            toast.error(
                typeof detail === 'string' ? detail : 'Could not send your note.'
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section
            id="contact"
            className="py-16 sm:py-20 lg:py-28 border-t border-border"
            data-testid="contact-section"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="grid gap-10 lg:grid-cols-2">
                    <div>
                        <p className="kicker">§ Get in touch</p>
                        <h2 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Have a brief?
                            <br />
                            <span className="text-primary">Let’s read it together.</span>
                        </h2>
                        <p className="mt-5 max-w-prose text-sm sm:text-base text-muted-foreground leading-relaxed">
                            I take on a small number of projects each quarter. Tell
                            me what you’re building, who it’s for, and when you’d
                            like to ship. I reply within 48 hours.
                        </p>
                        <div className="mt-8 space-y-3 text-sm">
                            <a
                                href="mailto:tinapallabi8@gmail.com"
                                className="flex items-center gap-3 group"
                                data-testid="contact-email-link"
                            >
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-foreground">
                                    <Mail className="h-4 w-4" />
                                </span>
                                <span className="group-hover:text-primary transition-colors">
                                    tinapallabi8@gmail.com
                                </span>
                            </a>
                            <a
                                href="tel:+918240888646"
                                className="flex items-center gap-3 group"
                            >
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-foreground">
                                    <Phone className="h-4 w-4" />
                                </span>
                                <span className="group-hover:text-primary transition-colors">
                                    +91 8240 888 646
                                </span>
                            </a>
                            <a
                                href="https://www.linkedin.com/"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-3 group"
                            >
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-foreground">
                                    <Linkedin className="h-4 w-4" />
                                </span>
                                <span className="group-hover:text-primary transition-colors">
                                    LinkedIn — Pallabi Bose
                                </span>
                            </a>
                        </div>
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        className="rounded-[var(--radius-card)] border border-border bg-card p-6 sm:p-8 shadow-card"
                    >
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label htmlFor="contact-name">Name</Label>
                                <Input
                                    id="contact-name"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({ ...form, name: e.target.value })
                                    }
                                    placeholder="Your name"
                                    data-testid="contact-name-input"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="contact-email">Email</Label>
                                <Input
                                    id="contact-email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm({ ...form, email: e.target.value })
                                    }
                                    placeholder="you@brand.com"
                                    data-testid="contact-email-input"
                                />
                            </div>
                        </div>
                        <div className="mt-4 space-y-1.5">
                            <Label>Project type</Label>
                            <Select
                                value={form.projectType}
                                onValueChange={(v) =>
                                    setForm({ ...form, projectType: v })
                                }
                            >
                                <SelectTrigger
                                    data-testid="contact-project-type-select"
                                >
                                    <SelectValue placeholder="What kind of writing?" />
                                </SelectTrigger>
                                <SelectContent>
                                    {PROJECT_TYPES.map((p) => (
                                        <SelectItem key={p} value={p}>
                                            {p}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="mt-4 space-y-1.5">
                            <Label htmlFor="contact-message">Message</Label>
                            <Textarea
                                id="contact-message"
                                rows={5}
                                value={form.message}
                                onChange={(e) =>
                                    setForm({ ...form, message: e.target.value })
                                }
                                placeholder="Tell me about the brief, the audience, and the timing."
                                data-testid="contact-message-textarea"
                            />
                        </div>
                        <Button
                            type="submit"
                            size="lg"
                            disabled={submitting}
                            className="mt-6 w-full rounded-full"
                            data-testid="contact-submit-button"
                        >
                            {submitting ? 'Sending…' : 'Send the brief →'}
                        </Button>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer className="border-t border-border py-10">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="font-heading text-base">
                    Pallabi Bose · Words that work. In two languages.
                </p>
                <p className="text-xs text-muted-foreground">
                    © {new Date().getFullYear()} Pallabi Bose. All rights
                    reserved.
                </p>
            </div>
        </footer>
    );
}

export default function PublicHome() {
    const [samples, setSamples] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('All');
    const [active, setActive] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const pdfRef = useRef(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        listSamples()
            .then((data) => {
                if (active) setSamples(data || []);
            })
            .catch(() => {
                if (active) toast.error('Could not load portfolio samples.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    async function handleDownloadPDF() {
        if (!pdfRef.current) {
            toast.error('PDF export is not ready yet. Try again in a moment.');
            return;
        }
        try {
            setDownloading(true);
            toast.info('Preparing your portfolio PDF…');
            await generatePortfolioPDF(
                pdfRef.current,
                'Pallabi-Bose-Portfolio.pdf'
            );
            toast.success('Portfolio PDF downloaded.');
        } catch (err) {
            console.error(err);
            toast.error('Could not generate PDF. Please try again.');
        } finally {
            setDownloading(false);
        }
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />
            <Hero
                onDownloadPDF={handleDownloadPDF}
                downloading={downloading}
            />
            <WhatIDo />
            <PortfolioGrid
                samples={samples}
                loading={loading}
                onOpen={setActive}
                filter={filter}
                setFilter={setFilter}
            />
            <About />
            <Clients />
            <Testimonials />
            <Contact />
            <Footer />
            <SampleModal sample={active} onClose={() => setActive(null)} />
            {/* Off-screen printable portfolio view for PDF export */}
            <div
                aria-hidden="true"
                style={{
                    position: 'fixed',
                    left: '-10000px',
                    top: 0,
                    width: '180mm',
                    pointerEvents: 'none',
                    opacity: 0,
                    zIndex: -1,
                }}
            >
                <PortfolioPDF ref={pdfRef} samples={samples} />
            </div>
        </div>
    );
}
